import { Typography, useTheme } from "@mui/joy";
import {
  Box,
  CircularProgress,
  List,
  ListItem,
  Paper,
  Collapse,
  IconButton,
  Container,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  TopicTree,
  umbracoApi,
  useGetStatsCoursesByCourseIdTopicsQuery,
  TopicStats,
} from "../../../store/api/umbraco-api";
import { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import React from "react";
import { palette } from "../../../theme";

const StudentStatisticsScreen = () => {
  const theme = useTheme();
  const { courseId } = useParams<{ courseId: string }>();
  const [statsById, setStatsById] = useState<Record<number, TopicStats>>({});
  const [expandedTopics, setExpandedTopics] = useState<Set<number>>(new Set());

  const { data: allTopicsRaw, isLoading: isLoadingTopics } =
    useGetStatsCoursesByCourseIdTopicsQuery(
      { courseId: Number(courseId) },
      { skip: !courseId }
    );

  const [fetchStats] = umbracoApi.useLazyGetStatsStudentTopicsByTopicIdQuery();

  const toggleTopicExpansion = (topicId: number) => {
    setExpandedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(topicId)) {
        next.delete(topicId);
      } else {
        next.add(topicId);
      }
      return next;
    });
  };

  // Fetch all stats once topics are loaded
  useEffect(() => {
    const fetchAllStats = async () => {
      if (!allTopicsRaw) return;

      const flattenTopics = (topics: TopicTree[]): TopicTree[] =>
        topics.flatMap((topic) => [
          topic,
          ...(topic.children ? flattenTopics(topic.children) : []),
        ]);

      const allTopicsFlat = flattenTopics(allTopicsRaw);
      const results = await Promise.all(
        allTopicsFlat.map((topic) =>
          topic.id
            ? fetchStats({ topicId: topic.id })
                .unwrap()
                .then((data) => ({
                  topicId: topic.id!,
                  stats: data,
                }))
            : null
        )
      );

      const statsMap = results.reduce((acc, result) => {
        if (result) {
          acc[result.topicId] = result.stats;
        }
        return acc;
      }, {} as Record<number, TopicStats>);

      setStatsById(statsMap);
    };

    fetchAllStats();
  }, [allTopicsRaw, fetchStats]);

  const overallStats = useMemo(() => {
    let totalAnswers = 0;
    let correctAnswers = 0;
    let maxLevel = 0;

    // Helper function to check if a topic is a leaf node (has no children)
    const isLeafNode = (topic: TopicTree): boolean => {
      return !topic.children || topic.children.length === 0;
    };

    // Helper function to get all leaf nodes
    const getLeafNodes = (topics: TopicTree[]): TopicTree[] => {
      return topics.flatMap((topic) => {
        if (isLeafNode(topic)) {
          return [topic];
        }
        return getLeafNodes(topic.children || []);
      });
    };

    // Get all leaf nodes from the topics tree
    const leafNodes = allTopicsRaw ? getLeafNodes(allTopicsRaw) : [];

    // Calculate stats only from leaf nodes
    leafNodes.forEach((topic) => {
      if (topic.id) {
        const stats = statsById[topic.id];
        if (stats) {
          totalAnswers += stats.totalAnswerCount || 0;
          correctAnswers += stats.correctAnswerCount || 0;
          maxLevel = Math.max(maxLevel, stats.level || 0);
        }
      }
    });

    const successRate =
      totalAnswers > 0 ? (correctAnswers / totalAnswers) * 100 : 0;

    return {
      totalAnswers,
      correctAnswers,
      successRate,
      maxLevel,
    };
  }, [statsById, allTopicsRaw]);

  return (
    <Box
      sx={{
        minHeight: "100%",
        overflow: "auto",
        pb: 4,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          color: "white",
          py: 3,
          px: { xs: 2, md: 4 },
          mb: 3,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            level="h2"
            sx={{
              fontSize: { xs: "1.5rem", md: "2rem" },
              fontWeight: "bold",
              mb: 1,
            }}
          >
            סטטיסטיקות אישיות
          </Typography>
          <Typography level="body-md" sx={{ opacity: 0.9 }}>
            צפייה בביצועים שלך בקורס
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Overall Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4, width: '100%'}}>
          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            <Card elevation={1} sx={{ width: '100%', bgcolor: palette.white }}>
              <CardContent>
                <Box>
                  <Typography
                    level="title-md"
                    sx={{ mb: 1, color: palette.gray }}
                  >
                    סה״כ שאלות
                  </Typography>
                  <Typography level="h3" sx={{ fontWeight: "bold", color: palette.purple}}>
                    {overallStats.totalAnswers}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            <Card elevation={1} sx={{ width: '100%', bgcolor: palette.white }}>
              <CardContent>
                <Box>
                  <Typography
                    level="title-md"
                    sx={{ mb: 1, color: palette.gray }}
                  >
                    סה״כ תשובות נכונות
                  </Typography>
                  <Typography level="h3" sx={{ fontWeight: "bold", color: palette.purple}}>
                    {overallStats.correctAnswers}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            <Card elevation={1} sx={{ width: '100%', bgcolor: palette.white }}>
              <CardContent>
                <Box>
                  <Typography
                    level="title-md"
                    sx={{ mb: 1, color: palette.gray }}
                  >
                    אחוז הצלחה
                  </Typography>
                  <Typography level="h3" sx={{ fontWeight: "bold", color: palette.purple}}>
                    {overallStats.successRate.toFixed(0)}%
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Topics List */}
        <Paper
          elevation={1}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: palette.white,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              bgcolor: palette.purple_light,
            }}
          >
            <Typography level="title-lg" sx={{ fontWeight: 600 }}>
              נושאים
            </Typography>
          </Box>

          {isLoadingTopics ? (
            <Box sx={{ p: 4, display: "flex", justifyContent: "flex-end" }}>
              <CircularProgress size={40} />
            </Box>
          ) : !allTopicsRaw?.length ? (
            <Box sx={{ p: 4, textAlign: "right" }}>
              <Typography level="body-lg" sx={{ color: "text.secondary" }}>
                אין נתונים זמינים עבור קורס זה
              </Typography>
            </Box>
          ) : (
            <>
              {/* Table Header */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Grid container>
                  <Grid item xs={6}>
                    <Typography
                      level="title-sm"
                      sx={{ fontWeight: 600, textAlign: "right", paddingRight: 2 }}
                    >
                       נושא
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Grid container>
                      <Grid item xs={3}>
                        <Typography
                          level="title-sm"
                          sx={{ fontWeight: 600, textAlign: "right" }}
                        >
                          שאלות שנענו
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography
                          level="title-sm"
                          sx={{ fontWeight: 600, textAlign: "right" }}
                        >
                          מתוכן נכונות
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography
                          level="title-sm"
                          sx={{ fontWeight: 600, textAlign: "right" }}
                        >
                          אחוז הצלחה
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography
                          level="title-sm"
                          sx={{ fontWeight: 600, textAlign: "right" }}
                        >
                          רמה 1-10
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>

              <List sx={{ p: 0 }}>
                {allTopicsRaw.map((topic) => {
                  const stats = topic.id ? statsById[topic.id] : undefined;
                  const successRate =
                    stats?.totalAnswerCount && stats.totalAnswerCount > 0
                      ? ((stats.correctAnswerCount || 0) /
                          stats.totalAnswerCount) *
                        100
                      : 0;
                  const levelDisplay = stats?.level || 0;

                  return (
                    <React.Fragment key={topic.id}>
                      <ListItem
                        sx={{
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          "&:last-child": {
                            borderBottom: "none",
                          },
                          py: 2,
                        }}
                      >
                        <Grid container alignItems="center">
                          <Grid item xs={6}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                              }}
                            >
                              <Box sx={{ width: 40, display: "flex", justifyContent: "center" }}>
                                {topic.children && topic.children.length > 0 && (
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      toggleTopicExpansion(topic.id || 0)
                                    }
                                  >
                                    {expandedTopics.has(topic.id || 0) ? (
                                      <ExpandLess />
                                    ) : (
                                      <ExpandMore />
                                    )}
                                  </IconButton>
                                )}
                              </Box>
                              <Typography
                                level="body-md"
                                sx={{ fontWeight: 600, textAlign: "right" }}
                              >
                                {topic.name}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6}>
                            <Grid container>
                              <Grid item xs={3}>
                                <Typography
                                  level="body-md"
                                  sx={{ textAlign: "right" }}
                                >
                                  {stats ? (
                                    stats.totalAnswerCount
                                  ) : (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "flex-start",
                                      }}
                                    >
                                      <CircularProgress size={16} />
                                    </Box>
                                  )}
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography
                                  level="body-md"
                                  sx={{ textAlign: "right" }}
                                >
                                  {stats ? (
                                    stats.correctAnswerCount
                                  ) : (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "flex-start",
                                      }}
                                    >
                                      <CircularProgress size={16} />
                                    </Box>
                                  )}
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography
                                  level="body-md"
                                  sx={{ textAlign: "right" }}
                                >
                                  {stats ? (
                                    `${successRate.toFixed(0)}%`
                                  ) : (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "flex-start",
                                      }}
                                    >
                                      <CircularProgress size={16} />
                                    </Box>
                                  )}
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography
                                  level="body-md"
                                  sx={{ textAlign: "right" }}
                                >
                                  {stats ? (
                                    levelDisplay
                                  ) : (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "flex-start",
                                      }}
                                    >
                                      <CircularProgress size={16} />
                                    </Box>
                                  )}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </ListItem>
                      {topic.children && topic.children.length > 0 && (
                        <Collapse
                          in={expandedTopics.has(topic.id || 0)}
                          timeout="auto"
                          unmountOnExit
                        >
                          <List component="div" disablePadding>
                            {topic.children.map((childTopic) => {
                              const childStats = childTopic.id
                                ? statsById[childTopic.id]
                                : undefined;
                              const childSuccessRate =
                                childStats?.totalAnswerCount &&
                                childStats.totalAnswerCount > 0
                                  ? ((childStats.correctAnswerCount || 0) /
                                      childStats.totalAnswerCount) *
                                    100
                                  : 0;
                              const childLevelDisplay = childStats?.level || 0;

                              return (
                                <ListItem
                                  key={childTopic.id}
                                  sx={{
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    bgcolor: "#f5f5f5",
                                    py: 2,
                                  }}
                                >
                                  <Grid container alignItems="center">
                                    <Grid item xs={6}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "flex-start",
                                        }}
                                      >
                                        <Box sx={{ width: 40 }} />
                                        <Typography
                                          level="body-md"
                                          sx={{ textAlign: "right" }}
                                        >
                                          {childTopic.name}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Grid container>
                                        <Grid item xs={3}>
                                          <Typography
                                            level="body-md"
                                            sx={{ textAlign: "right" }}
                                          >
                                            {childStats ? (
                                              childStats.totalAnswerCount
                                            ) : (
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent: "flex-start",
                                                }}
                                              >
                                                <CircularProgress size={16} />
                                              </Box>
                                            )}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                          <Typography
                                            level="body-md"
                                            sx={{ textAlign: "right" }}
                                          >
                                            {childStats ? (
                                              childStats.correctAnswerCount
                                            ) : (
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent: "flex-start",
                                                }}
                                              >
                                                <CircularProgress size={16} />
                                              </Box>
                                            )}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                          <Typography
                                            level="body-md"
                                            sx={{ textAlign: "right" }}
                                          >
                                            {childStats ? (
                                              `${childSuccessRate.toFixed(0)}%`
                                            ) : (
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent: "flex-start",
                                                }}
                                              >
                                                <CircularProgress size={16} />
                                              </Box>
                                            )}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                          <Typography
                                            level="body-md"
                                            sx={{ textAlign: "right" }}
                                          >
                                            {childStats ? (
                                              childLevelDisplay
                                            ) : (
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent: "flex-start",
                                                }}
                                              >
                                                <CircularProgress size={16} />
                                              </Box>
                                            )}
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </ListItem>
                              );
                            })}
                          </List>
                        </Collapse>
                      )}
                    </React.Fragment>
                  );
                })}
              </List>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default StudentStatisticsScreen;
